/**
 * The flashcard study session, as pure functions over a plain object.
 *
 * Kept apart from ui.js for the same reason format.js is: the interesting
 * rules here — what counts as finished, what "review the ones I forgot"
 * actually replays — are worth testing without a DOM or the AWS SDK.
 *
 * Every function returns a new session rather than mutating the one it was
 * given, so a caller can never half-apply a move.
 *
 * A session holds:
 *   order   the card indexes to show, in the order they will be shown
 *   at      how far through `order` we are
 *   flipped whether the current card is showing its definition
 *   marks   { cardIndex: 'known' | 'forgot' } for this pass only
 *   done    the last card in `order` has been marked
 */

const KNOWN = 'known';
const FORGOT = 'forgot';

export function createSession(cardCount) {
  return {
    order: Array.from({ length: Math.max(0, cardCount) }, (_, i) => i),
    at: 0,
    flipped: false,
    marks: {},
    done: false,
  };
}

export function flip(session) {
  return { ...session, flipped: !session.flipped };
}

/**
 * Mark the current card and advance. Marking the last card ends the session
 * rather than wrapping, and an ended session ignores further marks — so a
 * double-tap on Known cannot score a card that was never shown.
 */
export function mark(session, known) {
  if (session.done || session.order.length === 0) return session;

  const marks = { ...session.marks, [session.order[session.at]]: known ? KNOWN : FORGOT };
  const isLast = session.at + 1 >= session.order.length;

  return isLast
    ? { ...session, marks, flipped: false, done: true }
    : { ...session, marks, flipped: false, at: session.at + 1 };
}

/**
 * Fisher-Yates over the current deck. `random` is injectable so a test can
 * pin the shuffle; production passes nothing and gets Math.random.
 */
export function shuffle(session, random = Math.random) {
  const order = [...session.order];
  for (let i = order.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [order[i], order[j]] = [order[j], order[i]];
  }
  return { ...session, order, at: 0, flipped: false, marks: {}, done: false };
}

/**
 * Replay only the cards marked forgot, in the order they were shown.
 * Scores start again from zero — otherwise the summary would count a card
 * twice, once for forgetting it and once for then learning it.
 */
export function reviewForgot(session) {
  const order = forgotIndexes(session);
  return { ...session, order, at: 0, flipped: false, marks: {}, done: false };
}

export function forgotIndexes(session) {
  return session.order.filter((cardIndex) => session.marks[cardIndex] === FORGOT);
}

export function counts(session) {
  const marks = Object.values(session.marks);
  return {
    known: marks.filter((m) => m === KNOWN).length,
    forgot: marks.filter((m) => m === FORGOT).length,
  };
}

/** The index into the card array of the card on screen, or null when empty. */
export function currentIndex(session) {
  if (session.order.length === 0) return null;
  return session.order[Math.min(session.at, session.order.length - 1)];
}

/** "3 / 8" — clamped, so a finished session reads 8 / 8 rather than 9 / 8. */
export function position(session) {
  const total = session.order.length;
  if (total === 0) return '0 / 0';
  return `${Math.min(session.at + 1, total)} / ${total}`;
}

export function progressPercent(session) {
  const total = session.order.length;
  if (total === 0) return 0;
  const seen = session.done ? total : session.at;
  return Math.round((seen / total) * 100);
}
